import { Box, Button, Grid, Grid2, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import * as React from 'react';
import BIMViewer from '../Viewer/BimViewer';
import { useProject } from '../Contexts/Hooks/useProject';
import { useApi } from '../Api/useApi';
import { FileForOfferDto, GetModelSasUrisFromProjectIdResponse, GetOfferDataResponse, PrintRycApiClient, ProjectBudgetDTO, ProjectsRycApiClient, UpdateBudgetItemDescriptionsRequest } from '../Api/ApiServer';
import { useParams } from 'react-router';
import { Check, Close, Dashboard, EnergySavingsLeaf, Layers, Money, People, Print, Recycling, Security, Share, Timer, Verified, ViewStream, Visibility, WorkspacePremium } from '@mui/icons-material';
import ColoredGadget from '../Shared/ColoredGadget';
import { getCurrentGiff } from '../Shared/GetGiffBasedOnLandType';
import ViewFileDialog from '../Files/FileViews/ViewFileDialog';
import ProjectBudgetList from './ProjectBudgetList';
import { downloadBlob } from '../Shared/DownloadBlob';
import BimViewerDialog from '../Viewer/BimViewerDialog';
import BudgetItemDescriptionDialog from './Components/BudgetItemDescriptionDialog';
import { useSnackbar } from 'notistack';

export interface IProjectClientOfferProps {
    clientSide: boolean;
}

export default function ProjectClientOffer(props: IProjectClientOfferProps) {
    const { unauthorizedApi, authorizedApi } = useApi(ProjectsRycApiClient);
    const { enqueueSnackbar } = useSnackbar();
    const printApi = useApi(PrintRycApiClient);
    const [offerData, setOfferData] = React.useState<GetOfferDataResponse | null>();
    const [modelData, setModelData] = React.useState<GetModelSasUrisFromProjectIdResponse | null>(null);
    const theme = useTheme();
    const biggerThan500 = useMediaQuery(theme.breakpoints.up(500));
    const params = useParams();

    const load = React.useCallback(async () => {
        if (props.clientSide) {
            if (params.code) {
                const result = await unauthorizedApi.getOfferClient(params.code);
                if (result) {
                    setOfferData(result.offerData);
                    setModelData(result.modelData ?? null);
                }
            }
        }
        else {
            const result = await authorizedApi?.getOffer(Number(params['id']));
            if (result) setOfferData(result);
        }



    }, [unauthorizedApi, params, props.clientSide, authorizedApi]);
    const sendMail = React.useCallback(async (consultation: boolean) => {
        try {
            if (props.clientSide) {
                if (params.code) {
                    if (consultation)
                        await unauthorizedApi.getConsultationEmail(params.code);
                    else
                        await unauthorizedApi.getDiscountClient(params.code);
                    enqueueSnackbar('Email odeslán', { variant: 'success', autoHideDuration: 2000 });
                }


            }
        } catch {
            enqueueSnackbar('Chyba při odesílání emailu', { variant: 'error', autoHideDuration: 2000 });
        }




    }, [unauthorizedApi, params, props.clientSide, enqueueSnackbar]);
    React.useEffect(() => {
        load();
    }, [load]);
    const [viewerWidth, setViewerWidth] = React.useState(800);
    const boxRef = React.useRef<HTMLDivElement>(null);

    // Add this effect to update width when component mounts or window resizes
    React.useEffect(() => {
        const updateWidth = () => {
            if (boxRef.current) {
                // Account for padding (16px on each side)
                const width = boxRef.current.clientWidth - 32;
                setViewerWidth(width);
            }
        };

        // Initial measurement
        updateWidth();

        // Update on resize
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);
    const [selectedFile, setSelectedFile] = React.useState<FileForOfferDto | null>(null);
    const [openBimViewer, setOpenBimViewer] = React.useState(false);
    const [selectedBudgetItem, setSelectedBudgetItem] = React.useState<ProjectBudgetDTO | null>(null);
    // const {} = useProject();
    return (
        <Box overflow={'auto'} height={!props.clientSide ? 'calc(100vh - 144px)' : '100vh'} textAlign={'center'}>
            <ViewFileDialog open={selectedFile !== null} onClose={() => { setSelectedFile(null) }} fileDownloadUri={selectedFile?.fileDownloadUri} name={selectedFile?.name} />
            <BudgetItemDescriptionDialog
                open={selectedBudgetItem !== null}
                onClose={() => setSelectedBudgetItem(null)}
                budgetItem={selectedBudgetItem}
                onSave={(updatedItem) => {
                    authorizedApi?.updateBudgetItemDescriptions(new UpdateBudgetItemDescriptionsRequest({
                        id: updatedItem.id,
                        description: updatedItem.description,
                        contractDescription: updatedItem.contractDescription
                    })).then(() => {
                        enqueueSnackbar('Popis položky uložen', { variant: 'success', autoHideDuration: 2000 });
                        setOfferData(prev => {
                            if (!prev) return prev;
                            const updatedBudgetItems = prev.budgetItems?.map(item => item.id === updatedItem.id ? updatedItem : item);
                            return { ...prev, budgetItems: updatedBudgetItems } as GetOfferDataResponse;
                        });
                        setSelectedBudgetItem(null);
                    })
                    // TODO: Implement save endpoint

                }}
                readOnly={props.clientSide}
            />
            {/* {modelData && <BimViewerDialog modelData={modelData} open={openBimViewer} onClose={() => setOpenBimViewer(false)} />} */}
            {!openBimViewer && <Grid2 py={2} px={biggerThan500 ? 8 : 4} spacing={3} textAlign={'start'} maxWidth={1000} width={'100%'} container>
                <Grid2 size={{ xs: 12 }}>
                    <Typography variant="h2" color='primary'> Vaše nabídka</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Typography variant="h4" >{offerData?.projectName}</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Box display='flex' alignItems={'center'} flexDirection='row' justifyContent='space-between'>
                        <Typography variant="body1" >{offerData?.orderNumber}</Typography>
                        {!props.clientSide && <Button variant='outlined' onClick={() => {
                            authorizedApi?.getProjectClientOfferLink(Number(params['id'])).then((link) => {
                                if (link.offerUrl) {
                                    navigator.clipboard.writeText(link.offerUrl);
                                    enqueueSnackbar('Odkaz zkopírován do schránky', { variant: 'success', autoHideDuration: 2000 });
                                }
                            });
                        }} startIcon={<Share />}>Sdílet</Button>}
                        {props.clientSide && <Button onClick={() => {
                            printApi.unauthorizedApi.printBindingOfferReportClient(params.code!).then((file) =>
                                downloadBlob(file.data, 'nabidka.pdf'));
                        }} variant='outlined' startIcon={<Print />}>PDF</Button>}
                    </Box>
                </Grid2>
                <Grid2 size={{ md: 6, sm: 12, lg: 6, xl: 6 }}>
                    <Stack>
                        <Typography variant="body1" >Dobrý den,</Typography>
                        <Typography variant="body1" >připravili jsme pro Vás nabídku na základy pro Váš projekt. Nabídka je koncipována na míru Vámi poskytnutými informacemi.</Typography>
                        <br />
                        <Typography variant="body1" >V nabídce naleznete:</Typography>
                        <Stack mt={1} direction={'row'} spacing={2} border='1px solid' borderColor={'primary.main'} borderRadius={1} p={1}>
                            <Check color='primary' />
                            <Typography variant="body1" >Shrnutí</Typography>
                        </Stack>
                        <Stack mt={1} direction={'row'} spacing={2} border='1px solid' borderColor={'primary.main'} borderRadius={1} p={1}>
                            <Check color='primary' />
                            <Typography variant="body1" >BIM Model</Typography>
                        </Stack>
                        <Stack mt={1} direction={'row'} spacing={2} border='1px solid' borderColor={'primary.main'} borderRadius={1} p={1}>
                            <Check color='primary' />
                            <Typography variant="body1" >Rozpočet</Typography>
                        </Stack>
                        <Stack mt={1} direction={'row'} spacing={2} border='1px solid' borderColor={'primary.main'} borderRadius={1} p={1}>
                            <Check color='primary' />
                            <Typography variant="body1" >Parametry dodávky</Typography>
                        </Stack>
                        <Stack mt={1} direction={'row'} spacing={2} border='1px solid' borderColor={'primary.main'} borderRadius={1} p={1}>
                            <Check color='primary' />
                            <Typography variant="body1" >Dokumenty</Typography>
                        </Stack>

                    </Stack>
                </Grid2>
                <Grid2 size={{ md: 6, sm: 12, lg: 6, xl: 6 }}>
                    {offerData?.imageUri && <img style={{ maxWidth: '100%' }} src={offerData?.imageUri} alt='logo' />}
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Typography variant='h6' color='primary'>SHRNUTÍ</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <ColoredGadget icon={<Money color='primary' sx={{ fontSize: 30 }} />} upperText='Celková cena na klíč' lowerText={(offerData?.fullPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '') + ' Kč'} />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <ColoredGadget icon={<Timer color='primary' sx={{ fontSize: 30 }} />} upperText='Životnost založení' lowerText='Minimálně 100 let' />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <ColoredGadget icon={<WorkspacePremium color='primary' sx={{ fontSize: 30 }} />} upperText='Návaznost horní stavby' lowerText='Bezproblémová' />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <ColoredGadget icon={<Verified color='primary' sx={{ fontSize: 30 }} />} upperText='Návrh založení' lowerText='Ideální' />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Typography variant='body1'>Standardně provádíme dílo dle závazných norem ČSN. Naše přesnost se pohybuje v jednotkách absolutních milimetrů.</Typography>
                </Grid2>
                <Grid2 size={{ xl: 4, lg: 4, md: 6, sm: 12, xs: 12 }}>
                    <Stack alignItems={'center'}>
                        <Typography variant='h6' color='primary'>TEPELNÉ MOSTY</Typography>
                        <EnergySavingsLeaf sx={{ fontSize: 60 }} color='primary' />
                        <Typography fontWeight={'bold'} variant='body1'>Zrušení tepelných mostů</Typography>
                        <Typography variant='body1'>celoplošnou izolací základu</Typography>
                    </Stack>
                </Grid2>
                <Grid2 size={{ xl: 4, lg: 4, md: 6, sm: 12, xs: 12 }}>
                    <Stack alignItems={'center'}>
                        <Typography variant='h6' color='primary'>BEZCHYBNOST</Typography>
                        <Security sx={{ fontSize: 60 }} color='primary' />
                        <Typography fontWeight={'bold'} variant='body1'>Eliminace chyb</Typography>
                        <Typography variant='body1'>montáže díky prefabrikaci</Typography>
                    </Stack>
                </Grid2>
                <Grid2 size={{ xl: 4, lg: 4, md: 6, sm: 12, xs: 12 }}>
                    <Stack alignItems={'center'}>
                        <Typography variant='h6' color='primary'>EKOLOGICKY</Typography>
                        <Recycling sx={{ fontSize: 60 }} color='primary' />
                        <Typography fontWeight={'bold'} variant='body1'>Úspora 9t CO2</Typography>
                        <Typography variant='body1'>na každý základ</Typography>
                    </Stack>
                </Grid2>
                <Grid2 alignItems={'center'} size={{ xs: 12 }}>
                    <Box ref={boxRef}
                        boxShadow={4} height={320} width={'100%'} borderRadius={2} p={2}>
                        <BIMViewer embeded containerWidth={viewerWidth} modelData={modelData ?? undefined} />
                    </Box>
                </Grid2>
                <Grid2 textAlign={'center'} size={{ xs: 12 }}>
                    <Button variant='contained' color='primary' onClick={() => setOpenBimViewer(true)}>Otevřít 3D model</Button>
                </Grid2>
                <Grid2 mt={2} size={{ xs: 12 }}>
                    <Grid2 container>
                        <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                            <Grid2 spacing={1} container>
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography variant='h6' color='primary'>DATUMY</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography fontSize='small' variant='body1'>Zahájení stavby</Typography>
                                    <Typography variant='body1'>{offerData?.beginDate?.toLocaleDateString()}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <Typography fontSize='small' variant='body1'>Dokončení stavby</Typography>
                                    <Typography variant='body1'>{offerData?.endDate?.toLocaleDateString()}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography variant='h6' color='primary'>PARAMETRY</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography fontSize='small' variant='body1'>Délka pasů v metrech</Typography>
                                    <Typography variant='body1'>{offerData?.longitudeOfBelts?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography fontSize='small' variant='body1'>Plocha desky v m2</Typography>
                                    <Typography variant='body1'>{offerData?.area?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography fontSize='small' variant='body1'>Materiál podloží</Typography>
                                    <Typography variant='body1'>{offerData?.undergroundMaterial}</Typography>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                        <Grid2 mt={!biggerThan500 ? 2 : undefined} size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                            {offerData?.landType && <img style={{ width: !biggerThan500 ? '300px' : undefined, }} src={getCurrentGiff(offerData.landType)} />}
                        </Grid2>
                    </Grid2>
                </Grid2>
                <Grid2 textAlign={'center'} size={{ xs: 12 }}>
                    <Typography variant='body1'>Není Vám cokoliv jasné? Rádi Vám odpovíme na Vaše otázky online, nebo osobně. Poptejte u nás nezávaznou konzultaci.</Typography>
                </Grid2>
                {props.clientSide && <Grid2 textAlign={'center'} size={{ xs: 12 }}>
                    <Button variant='contained' onClick={() => sendMail(true)} startIcon={<People />} color='primary'>Nezávazná konzultace</Button>
                </Grid2>}
                <Grid2 size={{ xs: 12 }}>
                    <Typography color='primary' variant='h6'>DOKUMENTY</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Grid2 spacing={1} container>
                        {offerData?.files?.map((file) => <Grid2 borderBottom={'1px solid #C3C3C3'} textAlign={'center'} size={{ xs: 12 }}>
                            <Box display='flex' alignItems={'center'} justifyContent={'space-between'}>
                                <Typography variant='body2'>{file.name}</Typography>
                                <Button size='small' variant='text' onClick={() => { setSelectedFile(file) }} startIcon={<Visibility />} color='primary'>Otevřít</Button>
                            </Box>

                        </Grid2>)}
                    </Grid2>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Typography color='primary' variant='h6'>ROZPOČET</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <Box display='flex' alignItems={'center'} justifyContent={'space-between'}>
                        <Typography color='primary' variant='h5'>CELKEM</Typography>
                        <Typography color='primary' variant='h5'>{offerData?.fullPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč'}</Typography>
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <ProjectBudgetList onItemSelected={(item) => setSelectedBudgetItem(item)} budgetItems={offerData?.budgetItems ?? []} />
                </Grid2>
                {props.clientSide
                    && <Grid2 size={{ xs: 12 }} justifyContent={'center'} display='flex'>
                        <Button variant='contained' sx={{ width: '50%' }} onClick={() => sendMail(false)}>Požádat o slevu</Button>
                    </Grid2>}
            </Grid2>}
            {openBimViewer && modelData && <Box height={'100%'} bgcolor={'white'}><IconButton sx={{ position: 'absolute', right: '10px' }} onClick={() => setOpenBimViewer(false)}><Close /></IconButton><Box sx={{ position: 'absolute', top: '40px' }}><BIMViewer modelData={modelData} containerWidth={viewerWidth} /></Box></Box>}
        </Box >
    );
}
